import { useCallback, useMemo, useRef, useState } from "react";
import { Alert, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  AppFormField,
  AppFormGrid,
  AppSlideOver,
  PageFilterPanel,
  PageSectionCard,
} from "../../../components/layout";
import { PageHeader, PageListContainer } from "../../../components/Page";
import { AppAutocomplete, AppButton, AppCheckbox, AppTextField } from "../../../components/form";
import { GRID_FILTER_3 } from "../../../lib/ui/formLayout";
import DataTableWithPagination from "../../../components/DataTable";
import { anomalyService } from "../../../services/anomalyService";
import type { AnomalyIncident, AnomalyIncidentStatus } from "../../../types/anomaly";
import { showMessage } from "../../../context";

type StatusFilter = AnomalyIncidentStatus | "";

const STATUS_FILTERS: StatusFilter[] = ["", "OPEN", "ACKNOWLEDGED", "RESOLVED", "FALSE_POSITIVE", "AUTO_CLEARED"];
const EDITABLE_STATUSES: AnomalyIncidentStatus[] = [
  "OPEN",
  "ACKNOWLEDGED",
  "RESOLVED",
  "FALSE_POSITIVE",
  "AUTO_CLEARED",
];

export default function IncidentListPage() {
  const { t } = useTranslation();
  const tableRef = useRef<{ refresh: () => void } | null>(null);
  const [status, setStatus] = useState<StatusFilter>("");
  const [editing, setEditing] = useState<AnomalyIncident | null>(null);
  const [editStatus, setEditStatus] = useState<AnomalyIncidentStatus>("OPEN");
  const [editNote, setEditNote] = useState("");
  const [editSuppress, setEditSuppress] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUrl = useMemo(() => {
    const q = new URLSearchParams();
    if (status) q.set("status", status);
    const qs = q.toString();
    return qs ? `/anomalies/incidents?${qs}` : "/anomalies/incidents";
  }, [status]);

  const cols = useMemo(
    () => [
      {
        name: t("anomaly.definition"),
        selector: (row: AnomalyIncident) => row.definitionName || row.definitionId || "",
      },
      {
        name: t("anomaly.entity"),
        selector: (row: AnomalyIncident) => row.entityKey || "",
      },
      {
        name: t("anomaly.status"),
        selector: (row: AnomalyIncident) => row.status || "",
      },
      {
        name: t("anomaly.score"),
        selector: (row: AnomalyIncident) =>
          row.score != null ? Number(row.score).toFixed(2) : "",
      },
      {
        name: t("anomaly.grade"),
        selector: (row: AnomalyIncident) => row.grade || "",
      },
      {
        name: t("anomaly.lastSeen"),
        selector: (row: AnomalyIncident) => row.lastSeenAt || "",
      },
      {
        name: t("anomaly.occurrences"),
        selector: (row: AnomalyIncident) => String(row.occurrenceCount ?? ""),
      },
    ],
    [t]
  );

  const openEdit = useCallback((row: AnomalyIncident) => {
    if (!row.id) {
      showMessage(t("anomaly.incidentMissingId"), { color: "warning" });
      return;
    }
    setEditing(row);
    setEditStatus(row.status || "OPEN");
    setEditNote(row.note || "");
    setEditSuppress(Boolean(row.suppress));
    setError(null);
  }, [t]);

  const closeEdit = () => {
    if (saving) return;
    setEditing(null);
    setError(null);
  };

  const saveEdit = async () => {
    if (!editing?.id) return;
    setSaving(true);
    setError(null);
    try {
      await anomalyService.patchIncident(editing.id, {
        status: editStatus,
        note: editNote,
        suppress: editSuppress,
      });
      setEditing(null);
      showMessage(t("anomaly.incidentSaved"));
      tableRef.current?.refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : t("anomaly.incidentSaveFailed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageListContainer>
      <PageHeader
        title={t("anomaly.incidents")}
        subtitle={t("anomaly.incidentsSubtitle")}
      />
      <PageFilterPanel>
        <AppFormGrid spacing="compact">
          <AppFormField size={GRID_FILTER_3}>
            <AppAutocomplete<StatusFilter>
              label={t("anomaly.status")}
              options={STATUS_FILTERS}
              value={status}
              disableClearable
              autoHighlight
              openOnFocus
              getOptionLabel={(option) => (option === "" ? t("anomaly.allStatuses") : option)}
              onChange={(_, option) => setStatus(option ?? "")}
              renderInput={(params) => (
                <AppTextField {...params} label={t("anomaly.status")} margin="none" />
              )}
            />
          </AppFormField>
        </AppFormGrid>
      </PageFilterPanel>
      <PageSectionCard noPadding>
        <DataTableWithPagination
          key={fetchUrl}
          ref={tableRef}
          cols={cols}
          fetchUrl={fetchUrl}
          handleEdit={(row: AnomalyIncident) => openEdit(row)}
          searchCollapseToIcon
        />
      </PageSectionCard>

      <AppSlideOver
        open={Boolean(editing)}
        onClose={closeEdit}
        size="sm"
        title={t("anomaly.incidentEditTitle")}
        actions={
          <>
            <AppButton variant="outlined" onClick={closeEdit} disabled={saving}>
              {t("common.cancel")}
            </AppButton>
            <AppButton onClick={() => void saveEdit()} disabled={saving || !editing?.id}>
              {saving ? t("common.loading") : t("common.save")}
            </AppButton>
          </>
        }
      >
        {editing && (
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Stack spacing={0.25}>
              <Typography variant="caption" color="text.secondary">
                {t("anomaly.definition")}
              </Typography>
              <Typography variant="body2">
                {editing.definitionName || editing.definitionId || "—"}
              </Typography>
            </Stack>
            <Stack spacing={0.25}>
              <Typography variant="caption" color="text.secondary">
                {t("anomaly.entity")}
              </Typography>
              <Typography variant="body2">{editing.entityKey || "—"}</Typography>
            </Stack>
            <AppAutocomplete<AnomalyIncidentStatus>
              label={t("anomaly.status")}
              options={EDITABLE_STATUSES}
              value={editStatus}
              disableClearable
              autoHighlight
              openOnFocus
              getOptionLabel={(option) => option}
              onChange={(_, option) => setEditStatus(option ?? "OPEN")}
              renderInput={(params) => (
                <AppTextField {...params} label={t("anomaly.status")} margin="none" />
              )}
            />
            <AppTextField
              label={t("anomaly.incidentNote")}
              value={editNote}
              onChange={(e) => setEditNote(e.target.value)}
              multiline
              minRows={3}
              fullWidth
            />
            <AppCheckbox
              label={t("anomaly.incidentSuppress")}
              checked={editSuppress}
              onChange={(_, checked) => setEditSuppress(checked)}
            />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        )}
      </AppSlideOver>
    </PageListContainer>
  );
}
